import React, { useEffect, useRef, useState, useMemo } from 'react';
import styles from './Header.module.scss';
import { NavLink, useLocation } from 'react-router-dom';
import useHotkeysNav from '../../hooks/useHotkeysNav';

import { ReactComponent as Logo } from '../../assets/icons/fa.svg';
import { ReactComponent as HouseIcon } from '../../assets/icons/House_01.svg';
import { ReactComponent as ListIcon } from '../../assets/icons/Text_Align_Left.svg';
import { ReactComponent as FlagIcon } from '../../assets/icons/Flag.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/Settings.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/Calendar.svg';

const TOOLTIP_DELAY = 800;

const Header = () => {
  useHotkeysNav();
  const { pathname } = useLocation();

  const [isEventsEnabled, setIsEventsEnabled] = useState(
    localStorage.getItem('module_events_enabled') === 'true'
  );

  useEffect(() => {
    const handleSettingsChange = () => {
      setIsEventsEnabled(localStorage.getItem('module_events_enabled') === 'true');
    };

    window.addEventListener('settingsUpdated', handleSettingsChange);
    
    window.addEventListener('storage', handleSettingsChange);

    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsChange);
      window.removeEventListener('storage', handleSettingsChange);
    };
  }, []);

  const navItems = useMemo(() => {
    const base = [
      { path: '/dashboard', label: 'Главная', Icon: HouseIcon },
      { path: '/transactions', label: 'Транзакции', Icon: ListIcon },
      { path: '/goals', label: 'Цели', Icon: FlagIcon },
    ];
    
    if (isEventsEnabled) {
      base.push({ path: '/events', label: 'События', Icon: CalendarIcon });
    }
    
    base.push({ path: '/settings', label: 'Настройки', Icon: SettingsIcon });
    return base;
  }, [isEventsEnabled]);

  const activeIndex = navItems.findIndex(item => item.path === pathname);
  
  const [hoverIndex, setHoverIndex] = useState(null);

  const displayIndex = hoverIndex !== null ? hoverIndex : (activeIndex !== -1 ? activeIndex : 0);

  const [tip, setTip] = useState({ visible: false, text: '', top: 0 });
  const listRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const lastTargetRef = useRef(null);

  const hideTooltip = () => {
    setHoverIndex(null);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    lastTargetRef.current = null;
    setTip((t) => ({ ...t, visible: false }));
  };

  const scheduleTooltip = (e, text, index) => {
    setHoverIndex(index);
    if (!listRef.current) return;

    const target = e.currentTarget;
    lastTargetRef.current = target;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    const updatePosition = () => {
      const listRect = listRef.current.getBoundingClientRect();
      const elRect = target.getBoundingClientRect();
      const top = elRect.top - listRect.top + elRect.height / 2;
      setTip((t) => ({ ...t, text, top }));
    };

    updatePosition();

    if (!tip.visible) {
      hoverTimerRef.current = setTimeout(() => {
        if (lastTargetRef.current !== target) return;
        setTip((t) => ({ ...t, visible: true, text }));
      }, TOOLTIP_DELAY);
    } else {
      setTip((t) => ({ ...t, text }));
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.header__inner}>
        <NavLink to='/' className={styles.logo__container}>
          <Logo className={styles.logo} />
        </NavLink>

        <ul
          ref={listRef}
          className={styles.header__list}
          onMouseLeave={hideTooltip}
          style={{ '--index': displayIndex }}
        >
          <div className={styles.header__indicator} />

          {navItems.map((item, idx) => (
            <NavLink 
              key={item.path} 
              to={item.path} 
              className={styles.header__nav}
            >
              {({ isActive }) => (
                <li className={`${styles.header__item} ${isActive ? styles.header__item_active : ''}`}>
                  <span
                    className={`${styles.header__link} ${isActive ? styles.header__link_active : ''}`}
                    onMouseEnter={(e) => scheduleTooltip(e, item.label, idx)}
                  >
                    <item.Icon className={styles.header__icon} />
                  </span>
                </li>
              )}
            </NavLink>
          ))}

          <div
            className={`${styles.header__tooltip} ${tip.visible ? styles.header__tooltip_visible : ''}`}
            style={{ transform: `translateY(${tip.top}px)` }}
          >
            {tip.text}
          </div>
        </ul>
      </div>
    </header>
  );
};

export default Header;